/**
 * Gemeinsames Erscheinungsbild.
 *
 * Ton: duestere Seekarte bei Nacht. Wenig Farbe, viel Tiefe, und die wenigen
 * hellen Akzente gehoeren dem, was gerade wichtig ist.
 */

export const PALETTE = {
  /** Hintergrund ausserhalb der Karte. */
  abyss: 0x05080d,
  panel: 0x0a1119,
  panelEdge: 0x1c2c3a,

  ink: '#c8d6e5',
  inkDim: '#7f95a8',
  inkFaint: '#4a6572',

  /** Kuestenlinien — warmes Pergament gegen das kalte Wasser. */
  coast: 0xe4d4ad,
  /** Das Dreieck selbst. */
  triangle: 0xd45a62,

  base: 0x8fd9c4,
  landmark: 0xe0b070,
  vessel: 0xf2f6fa,
  course: 0x6fa8c9,
} as const;

/**
 * Dieselben Akzente als CSS-Zeichenketten.
 *
 * Phaser erwartet fuer Grafikobjekte Zahlen, fuer Text aber CSS-Farben. Beides
 * getrennt zu halten verhindert, dass versehentlich ein Zahlenwert als
 * Textfarbe gesetzt wird — der Compiler faengt das dann ab.
 */
export const TEXT_COLOUR = {
  coast: '#e4d4ad',
  base: '#8fd9c4',
  landmark: '#e0b070',
  danger: '#c9736f',
  course: '#6fa8c9',
} as const;

export const FONT = {
  display: 'Georgia, "Times New Roman", serif',
  mono: '"Consolas", "SF Mono", monospace',
} as const;

/**
 * Schriftgroesse.
 *
 * Das Spiel ist eine Textseite mit Karte, und die Grundgroessen liegen bei 11
 * bis 13 Pixel — auf einem hochaufloesenden Bildschirm ist das fuer manche
 * schlicht nicht lesbar. Alle Textstile gehen deshalb durch `px()`, und das
 * Vergroessern ist ein Faktor darauf statt eines zweiten Satzes Zahlen.
 *
 * Die Groesse steht als Modulzustand und nicht in der Registry, weil sie beim
 * Erzeugen jedes einzelnen Textobjekts gebraucht wird — eine Szene
 * durchzureichen waere an dieser Stelle nur Umstand.
 */
export const TEXT_SCALES = [1, 1.15, 1.3, 1.5, 1.75, 2] as const;

export type TextScale = (typeof TEXT_SCALES)[number];

/**
 * Voreinstellung.
 *
 * Nicht 100 %. Die Grundgroessen des Spiels liegen bei elf bis dreizehn
 * Pixeln — das stammt aus einer Zeit, als es nur um die Anmutung einer
 * Seekarte ging, und ist auf einem heutigen Bildschirm schlicht zu klein zum
 * Lesen. Ein Spiel, das fast nur aus Text besteht, darf nicht damit anfangen,
 * dass man die Einstellungen sucht.
 */
export const DEFAULT_TEXT_SCALE: TextScale = 1.3;

let textScale: TextScale = DEFAULT_TEXT_SCALE;

export function getTextScale(): TextScale {
  return textScale;
}

export function setTextScale(scale: TextScale): void {
  textScale = scale;
}

/**
 * Eine Schriftgroesse in Pixeln, skaliert.
 *
 * Gerundet, weil Bruchteile eines Pixels je nach Browser unterschiedlich
 * gerastert werden und Zeilen dann bei gleichem Text verschieden hoch
 * ausfallen — was die aus Texthoehen gerechneten Layouts springen liesse.
 */
export const px = (size: number): string => `${Math.round(size * textScale)}px`;

/** Textstil fuer Ueberschriften. */
export const titleStyle = (size: number) => ({
  fontFamily: FONT.display,
  fontSize: px(size),
  color: PALETTE.ink,
});

/** Textstil fuer Anzeigen und Messwerte. */
export const readoutStyle = (size: number, dim = false) => ({
  fontFamily: FONT.mono,
  fontSize: px(size),
  color: dim ? PALETTE.inkDim : PALETTE.ink,
});

/**
 * Textstil fuer Beschriftungen — kleine, ruhige Zeilen ueber Listen und
 * Feldern. Fasst zusammen, was vorher an einem Dutzend Stellen als
 * `fontFamily`/`fontSize`/`color` von Hand stand und die Skalierung deshalb
 * nicht mitbekommen haette.
 */
export const labelStyle = (size = 11, colour: string = PALETTE.inkFaint) => ({
  fontFamily: FONT.mono,
  fontSize: px(size),
  color: colour,
});

/**
 * Bewegung reduzieren.
 *
 * Pulsierende Marker, Kamerafahrten und Einblendungen sind fuer manche
 * Menschen mehr als Zierat — sie loesen Schwindel oder Uebelkeit aus. Die
 * Voreinstellung folgt deshalb dem Betriebssystem (`prefers-reduced-motion`);
 * in den Einstellungen laesst sie sich in beide Richtungen ueberstimmen.
 *
 * Modulzustand aus demselben Grund wie die Schriftgroesse: Gebraucht wird der
 * Wert beim Erzeugen jeder einzelnen Animation.
 */
function systemPrefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

let reducedMotion = systemPrefersReducedMotion();

export function getReducedMotion(): boolean {
  return reducedMotion;
}

export function setReducedMotion(value: boolean): void {
  reducedMotion = value;
}

/** Dauer einer Animation — null, wenn Bewegung reduziert ist. */
export const motionMs = (ms: number): number => (reducedMotion ? 0 : ms);
